/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Autocomplete,
  Button,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useEffect, useRef, useState } from "react";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import Config from "../../Utils/Config";
import {
  DeviceModel,
  LocationModel,
  BrandModel,
  ModelModel,
} from "../../Utils/Models";
import { Vocabulary } from "../../Utils/Vocabulary";
import AddOrUpdateModel from "../GenericComponents/AddOrUpdateModel";
import { onChangeDatePicker, onChangeTextField } from "../../Utils/Utils";
import moment from "moment";
import AddAndShowImages from "../GenericComponents/AddAndShowImages";
import { toast } from "react-toastify";
import { getData } from "../../Services/getData";
import { urlEnum } from "../../Utils/UrlEnum";
import { postData } from "../../Services/postData";
import Brand from "../Brands/Brand";
import Model from "../Models/Model";
import LocationMainPage from "../Locations/LocationMainPage";

type DeviceProps = {
  editDevice: DeviceModel | null;
  open: boolean;
  addOrUpdateDevice: (
    device: DeviceModel,
    clientFiles: any[],
    beforeFiles: any[],
    afterFiles: any[],
  ) => void;
  onClose: () => void;
};

export default function Device(props: DeviceProps) {
  const { editDevice, open, addOrUpdateDevice, onClose } = props;
  const [device, setDevice] = useState<DeviceModel>(new DeviceModel());
  const [pressOnSubmit, setPressOnSubmit] = useState(false);
  const [clientFiles, setClientFiles] = useState<any[]>([]);
  const [beforeFiles, setBeforeFiles] = useState<any[]>([]);
  const [afterFiles, setAfterFiles] = useState<any[]>([]);
  const [locations, setLocations] = useState<Array<LocationModel>>([]);
  const [brands, setBrands] = useState<Array<BrandModel>>([]);
  const [models, setModels] = useState<Array<ModelModel>>([]);
  const [openBrandDrawer, setOpenBrandDrawer] = useState(false);
  const [openModelDrawer, setOpenModelDrawer] = useState(false);
  const [openLocationDrawer, setOpenLocationDrawer] = useState(false);
  const [brandFocused, setBrandFocused] = useState(false);
  const [modelFocused, setModelFocused] = useState(false);
  const [locationFocused, setLocationFocused] = useState(false);
  const ref: any = useRef();

  const buttons = (
    <Button
      onClick={resetDevice}
      variant="contained"
      style={{ marginBottom: 10, marginTop: 10, color: "white" }}
      fullWidth
      color="secondary"
    >
      {Vocabulary.reset}
    </Button>
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [locationsData, brandsData, modelsData] = await Promise.all([
          getData(urlEnum.locations),
          getData(urlEnum.brands),
          getData(urlEnum.deviceModels),
        ]);
        setLocations(locationsData.data.result);
        setBrands(brandsData.data.result);
        setModels(modelsData.data.result);
      } catch (error) {
        toast.error(`${error}`);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (editDevice) {
      setDevice(editDevice);
    } else {
      setDevice(new DeviceModel());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editDevice]);

  useEffect(() => {
    if (pressOnSubmit) {
      ref.current.submit();
      handleChangePressOnSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pressOnSubmit]);

  function handleChangePressOnSubmit() {
    setPressOnSubmit(!pressOnSubmit);
  }

  function resetDevice() {
    if (editDevice) {
      setDevice(editDevice);
    } else {
      setDevice(new DeviceModel());
    }
    setClientFiles([]);
    setBeforeFiles([]);
    setAfterFiles([]);
  }

  // --- Client photos handlers ---
  function handleChangeClientFiles(newFiles: any) {
    setClientFiles((prev) => [...prev, ...newFiles]);
  }
  function handleDeleteClientImages(newImageList: any, newFileList: any) {
    setDevice({ ...device, images: newImageList.map((img: any) => img.path) });
    setClientFiles(newFileList);
  }
  function displayDropzoneClientImages(images: any) {
    setDevice({ ...device, images });
  }

  // --- Before photos handlers ---
  function handleChangeBeforeFiles(newFiles: any) {
    setBeforeFiles((prev) => [...prev, ...newFiles]);
  }
  function handleDeleteBeforeImages(newImageList: any, newFileList: any) {
    setDevice({
      ...device,
      servicePhotosBefore: newImageList.map((img: any) => img.path),
    });
    setBeforeFiles(newFileList);
  }
  function displayDropzoneBeforeImages(images: any) {
    setDevice({ ...device, servicePhotosBefore: images });
  }

  // --- After photos handlers ---
  function handleChangeAfterFiles(newFiles: any) {
    setAfterFiles((prev) => [...prev, ...newFiles]);
  }
  function handleDeleteAfterImages(newImageList: any, newFileList: any) {
    setDevice({
      ...device,
      servicePhotosAfter: newImageList.map((img: any) => img.path),
    });
    setAfterFiles(newFileList);
  }
  function displayDropzoneAfterImages(images: any) {
    setDevice({ ...device, servicePhotosAfter: images });
  }

  function addNewBrand(brand: BrandModel | null) {
    if (!brand) return;
    postData(urlEnum.brands, { name: brand.name }).then((res: any) => {
      if (res?.data?.result) {
        const newBrand = res.data.result;
        setBrands((prev) => [...prev, newBrand]);
        setDevice((prev) => ({ ...prev, brand: newBrand, model: null }));
        setOpenBrandDrawer(false);
      }
    });
  }

  function addNewModel(model: ModelModel | null) {
    if (!model) return;
    const brandId = model.brand?._id || device.brand?._id;
    postData(urlEnum.deviceModels, { name: model.name, brand: brandId }).then(
      (res: any) => {
        if (res?.data?.result) {
          const newModel = { ...res.data.result, brand: brandId };
          setModels((prev) => [...prev, newModel]);
          setDevice((prev) => ({ ...prev, model: newModel }));
          setOpenModelDrawer(false);
        }
      },
    );
  }

  function addNewLocation(location: LocationModel | null) {
    if (!location) return;
    const { _id, ...locationData } = location;
    postData(urlEnum.locations, locationData).then((res: any) => {
      if (res?.data?.result) {
        const newLocation = res.data.result;
        setLocations((prev) => [...prev, newLocation]);
        setDevice((prev) => ({ ...prev, location: newLocation }));
        setOpenLocationDrawer(false);
      }
    });
  }

  function getFilteredModels() {
    if (!device.brand?._id) return [];
    return models.filter((m: any) => {
      const brandId = m.brand?._id || m.brand;
      return brandId === device.brand?._id;
    });
  }

  return (
    <>
      <ValidatorForm
        ref={ref}
        onSubmit={() => addOrUpdateDevice(device, clientFiles, beforeFiles, afterFiles)}
      >
        <AddOrUpdateModel
          open={open}
          title={Vocabulary.device}
          onClose={onClose}
          isSubmit={true}
          addOrUpdateItem={() => null}
          handleChangePressOnSubmit={handleChangePressOnSubmit}
          buttons={buttons ? buttons : null}
        >
          <Grid container spacing={1}>
            <Grid item xs={12} sm={12} md={6} lg={6}>
              <TextValidator
                size="small"
                variant="outlined"
                name="serialNumber"
                id="serialNumber"
                label={Vocabulary.serialNumber}
                fullWidth
                validators={["required"]}
                errorMessages={[Vocabulary.requiredField]}
                value={device.serialNumber}
                onChange={(event) =>
                  setDevice(onChangeTextField(event, device))
                }
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6}>
              <TextValidator
                size="small"
                variant="outlined"
                name="name"
                id="name"
                label={Vocabulary.name}
                fullWidth
                validators={["required"]}
                errorMessages={[Vocabulary.requiredField]}
                value={device.name}
                onChange={(event) =>
                  setDevice(onChangeTextField(event, device))
                }
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6}>
              <TextValidator
                size="small"
                variant="outlined"
                name="code"
                id="code"
                label={Vocabulary.code}
                fullWidth
                validators={["required"]}
                errorMessages={[Vocabulary.requiredField]}
                value={device.code}
                onChange={(event) =>
                  setDevice(onChangeTextField(event, device))
                }
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  name="purchaseDate"
                  label={Vocabulary.purchaseDate}
                  slotProps={{
                    textField: { style: { width: "100%" }, size: "small" },
                  }}
                  value={
                    typeof device.purchaseDate === "string"
                      ? moment(device.purchaseDate)
                      : device.purchaseDate
                  }
                  onChange={(e: any) =>
                    setDevice(onChangeDatePicker(e, device, "purchaseDate"))
                  }
                  format={Config.momentEUDateFormat}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <Autocomplete
                  size="small"
                  id="brand"
                  freeSolo={false}
                  disablePortal
                  getOptionLabel={(option: any) => option.name || ""}
                  isOptionEqualToValue={(option: any, value: any) =>
                    option._id === value._id
                  }
                  options={brands}
                  value={device.brand}
                  onChange={(event: any, value: any) => {
                    setDevice({ ...device, brand: value, model: null });
                  }}
                  onFocus={() => setBrandFocused(true)}
                  onBlur={() => setTimeout(() => setBrandFocused(false), 200)}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="brand"
                      label={Vocabulary.brand}
                      variant="outlined"
                    />
                  )}
                />
                <Tooltip title={Vocabulary.add}>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => setOpenBrandDrawer(true)}
                    style={{ visibility: brandFocused ? "visible" : "hidden" }}
                  >
                    <AddIcon />
                  </IconButton>
                </Tooltip>
              </div>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <Autocomplete
                  size="small"
                  id="model"
                  freeSolo={false}
                  disablePortal
                  disabled={!device.brand}
                  getOptionLabel={(option: any) => option.name || ""}
                  isOptionEqualToValue={(option: any, value: any) =>
                    option._id === value._id
                  }
                  options={getFilteredModels()}
                  value={device.model}
                  onChange={(event: any, value: any) => {
                    setDevice({ ...device, model: value });
                  }}
                  onFocus={() => setModelFocused(true)}
                  onBlur={() => setTimeout(() => setModelFocused(false), 200)}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="model"
                      label={Vocabulary.model}
                      variant="outlined"
                    />
                  )}
                />
                <Tooltip title={Vocabulary.add}>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => setOpenModelDrawer(true)}
                    style={{
                      visibility:
                        modelFocused && device.brand ? "visible" : "hidden",
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </Tooltip>
              </div>
            </Grid>
            <Grid item xs={12}>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <Autocomplete
                  size="small"
                  id="location"
                  freeSolo={false}
                  disablePortal
                  getOptionLabel={(option: any) => `${option.name}`}
                  isOptionEqualToValue={(option: any, value: any) =>
                    option._id === value._id
                  }
                  options={locations}
                  value={device.location}
                  onChange={(event: any, value: any) => {
                    setDevice({ ...device, location: value });
                  }}
                  onFocus={() => setLocationFocused(true)}
                  onBlur={() => setTimeout(() => setLocationFocused(false), 200)}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="location"
                      label={Vocabulary.location || "Location"}
                      variant="outlined"
                    />
                  )}
                />
                <Tooltip title={Vocabulary.add}>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => setOpenLocationDrawer(true)}
                    style={{ visibility: locationFocused ? "visible" : "hidden" }}
                  >
                    <AddIcon />
                  </IconButton>
                </Tooltip>
              </div>
            </Grid>

            {/* Client photos */}
            <Grid item xs={12}>
              <div style={{ border: "1px solid #1976d2", borderRadius: 8, overflow: "hidden" }}>
                <div style={{ backgroundColor: "#e3f2fd", borderBottom: "1px solid #1976d2", padding: "6px 12px", display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", backgroundColor: "#1976d2", flexShrink: 0 }} />
                  <Typography variant="subtitle2" style={{ fontWeight: "bold", color: "#1976d2" }}>
                    {Vocabulary.clientPhotos}
                  </Typography>
                </div>
                <div style={{ padding: "10px 12px" }}>
                  <AddAndShowImages
                    inline
                    images={device.images}
                    files={clientFiles}
                    handleDeleteImages={handleDeleteClientImages}
                    handleChangeFiles={handleChangeClientFiles}
                    displayDropzoneImages={displayDropzoneClientImages}
                  />
                </div>
              </div>
            </Grid>

            {/* Service photos group */}
            <Grid item xs={12}>
              <div style={{ border: "1px solid #bdbdbd", borderRadius: 8, overflow: "hidden" }}>
                <div style={{ backgroundColor: "#f5f5f5", borderBottom: "1px solid #bdbdbd", padding: "6px 12px" }}>
                  <Typography variant="subtitle1" style={{ fontWeight: "bold", color: "#424242" }}>
                    {Vocabulary.servicePhotos}
                  </Typography>
                </div>
                <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 12 }}>

                  {/* Before */}
                  <div style={{ border: "1px solid #ed6c02", borderRadius: 8, overflow: "hidden" }}>
                    <div style={{ backgroundColor: "#fff3e0", borderBottom: "1px solid #ed6c02", padding: "6px 12px", display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", backgroundColor: "#ed6c02", flexShrink: 0 }} />
                      <Typography variant="subtitle2" style={{ fontWeight: "bold", color: "#ed6c02" }}>
                        {Vocabulary.photosBefore}
                      </Typography>
                    </div>
                    <div style={{ padding: "10px 12px" }}>
                      <AddAndShowImages
                        inline
                        images={device.servicePhotosBefore}
                        files={beforeFiles}
                        handleDeleteImages={handleDeleteBeforeImages}
                        handleChangeFiles={handleChangeBeforeFiles}
                        displayDropzoneImages={displayDropzoneBeforeImages}
                      />
                    </div>
                  </div>

                  {/* After */}
                  <div style={{ border: "1px solid #2e7d32", borderRadius: 8, overflow: "hidden" }}>
                    <div style={{ backgroundColor: "#e8f5e9", borderBottom: "1px solid #2e7d32", padding: "6px 12px", display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", backgroundColor: "#2e7d32", flexShrink: 0 }} />
                      <Typography variant="subtitle2" style={{ fontWeight: "bold", color: "#2e7d32" }}>
                        {Vocabulary.photosAfter}
                      </Typography>
                    </div>
                    <div style={{ padding: "10px 12px" }}>
                      <AddAndShowImages
                        inline
                        images={device.servicePhotosAfter}
                        files={afterFiles}
                        handleDeleteImages={handleDeleteAfterImages}
                        handleChangeFiles={handleChangeAfterFiles}
                        displayDropzoneImages={displayDropzoneAfterImages}
                      />
                    </div>
                  </div>

                </div>
              </div>
            </Grid>
          </Grid>
        </AddOrUpdateModel>
      </ValidatorForm>
      {openBrandDrawer && (
        <Brand
          editBrand={null}
          open={openBrandDrawer}
          addOrUpdateBrand={addNewBrand}
          onClose={() => setOpenBrandDrawer(false)}
        />
      )}
      {openModelDrawer && (
        <Model
          editModel={
            device.brand ? { ...new ModelModel(), brand: device.brand } : null
          }
          open={openModelDrawer}
          addOrUpdateModel={addNewModel}
          onClose={() => setOpenModelDrawer(false)}
        />
      )}
      {openLocationDrawer && (
        <LocationMainPage
          editLocation={null}
          open={openLocationDrawer}
          addOrUpdateLocation={addNewLocation}
          onClose={() => setOpenLocationDrawer(false)}
        />
      )}
    </>
  );
}
